import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { colors } from '@ardoq/design-tokens';
import { RepresentationData } from '@ardoq/data-model';
import {
  formatOtherLabel,
  GROUP_HEADER_FONT_HEIGHT,
  OtherLabel,
  Rect,
  SvgComponentRepresentation,
  TAB_HEIGHT,
} from '@ardoq/graph';
import type { VisualDiffClassName } from '@ardoq/common-helpers';
import {
  ensureContrast,
  visualDiffBackgroundColors,
  visualDiffColors,
} from '@ardoq/color-helpers';
import { Size, TextMeasurePolicy, TextWrapping } from '@ardoq/yfiles';
import {
  HEADER_ICON_SIZE,
  GROUP_PADDING,
  BLOCK_DIAGRAM_GROUP_LABEL_TEXT_PARAMS,
  BLOCK_DIAGRAM_GROUP_HEADER_AFTER_EXPANDER_X,
} from './consts';
import { addText } from 'yfilesExtensions/addText';
import { getElementOpacityIfTransparentized } from './utils';
import { OTHER_LABEL_TEXT_ELEMENT_HORIZONTAL_PADDING } from './yFilesExtensions/labels/consts';
import {
  BLOCK_DIAGRAM_OTHER_LABEL_FONT,
  BLOCK_DIAGRAM_OTHER_LABEL_MAX_SIZE,
  LABEL_VERTICAL_PADDING,
} from '../../../yfilesExtensions/styles/consts';
import { applySpecialStyle } from './yFilesExtensions/labels/labelUtils';

const ICON_Y = (TAB_HEIGHT - GROUP_HEADER_FONT_HEIGHT) / 2;

type OtherLabelTextElementProps = {
  label: string;
  x: number;
  y: number;
  isTransparentized: boolean;
  isPlaceholder: boolean;
};
const OtherLabelTextElement = ({
  label,
  x,
  y,
  isTransparentized,
  isPlaceholder,
}: OtherLabelTextElementProps) => {
  const elementRef = useRef<SVGTextElement>(null);

  useEffect(() => {
    if (elementRef.current) {
      addText({
        targetElement: elementRef.current,
        text: label,
        font: BLOCK_DIAGRAM_OTHER_LABEL_FONT,
        maximumSize: BLOCK_DIAGRAM_OTHER_LABEL_MAX_SIZE,
        wrapping: TextWrapping.WRAP_WORD_ELLIPSIS,
        measurePolicy: TextMeasurePolicy.SVG,
      });

      applySpecialStyle(elementRef.current, isPlaceholder, isTransparentized);
    }
  }, [label, isTransparentized, isPlaceholder, x, y]);

  return <text ref={elementRef} textAnchor={'start'} y={y} x={x}></text>;
};
type OtherLabelBackgroundProperties = {
  size: Size;
  backgroundY: number;
};
const OtherLabelBackground = ({
  size,
  backgroundY,
}: OtherLabelBackgroundProperties) => (
  <rect
    width={size.width + OTHER_LABEL_TEXT_ELEMENT_HORIZONTAL_PADDING * 2}
    height={size.height}
    rx={4}
    ry={4}
    stroke={colors.blue200}
    strokeWidth={1}
    fill={colors.blue50}
    x={BLOCK_DIAGRAM_GROUP_HEADER_AFTER_EXPANDER_X}
    y={backgroundY}
  />
);

type OtherLabelData = {
  otherLabel: OtherLabel;
  size: Size;
  previousHeight: number;
};
type OtherLabelProps = OtherLabelData & {
  groupLabelHeight: number;
  isTransparentized: boolean;
};
const OtherLabelComponent = ({
  otherLabel,
  size,
  previousHeight,
  groupLabelHeight,
  isTransparentized,
}: OtherLabelProps) => {
  const label = formatOtherLabel(otherLabel);
  if (!label) {
    return null;
  }
  const yOffset = groupLabelHeight + LABEL_VERTICAL_PADDING * 2;
  const backgroundY = yOffset + previousHeight;

  return (
    <g>
      <OtherLabelBackground size={size} backgroundY={backgroundY} />
      <OtherLabelTextElement
        label={label}
        isTransparentized={isTransparentized}
        isPlaceholder={false}
        x={
          BLOCK_DIAGRAM_GROUP_HEADER_AFTER_EXPANDER_X +
          OTHER_LABEL_TEXT_ELEMENT_HORIZONTAL_PADDING
        }
        y={backgroundY}
      />
    </g>
  );
};

const CollapsibleGroupHeaderLabel = styled.text<{
  $isTransparentized: boolean;
}>`
  fill: ${colors.grey950};
  fill-opacity: ${({ $isTransparentized }) =>
    $isTransparentized ? '0.5' : '1'};
  dominant-baseline: central;
`;

interface GroupHeaderLabelProperties {
  representationData: RepresentationData | null;
  representationColor: string | undefined;
  text: string;
  fieldLabelAndValue?: string | null;
  groupLabelHeight: number;
  groupLabelWidth: number;
  groupNameMaxHeight: number;
  labelX: number;
  visualDiffClass: VisualDiffClassName | '';
  isTransparentized: boolean;
  otherLabelsData?: OtherLabelData[];
}
const GroupHeaderLabel = ({
  representationData,
  representationColor,
  text,
  fieldLabelAndValue,
  groupLabelHeight,
  groupLabelWidth,
  groupNameMaxHeight,
  labelX,
  visualDiffClass,
  isTransparentized,
  otherLabelsData,
}: GroupHeaderLabelProperties) => {
  const textElementRef = useRef<SVGTextElement>(null);

  useEffect(() => {
    if (textElementRef.current) {
      addText({
        targetElement: textElementRef.current,
        text,
        maximumSize: new Size(groupLabelWidth, groupNameMaxHeight),
        ...BLOCK_DIAGRAM_GROUP_LABEL_TEXT_PARAMS,
      });
    }
  }, [text, groupLabelWidth, groupNameMaxHeight]);

  const fill = representationData
    ? visualDiffClass
      ? ensureContrast(
          visualDiffBackgroundColors[visualDiffClass],
          visualDiffColors[visualDiffClass]
        )
      : representationColor
    : undefined;
  const fontSize = GROUP_HEADER_FONT_HEIGHT;

  const componentRepresentationBounds = representationData
    ? new Rect(
        BLOCK_DIAGRAM_GROUP_HEADER_AFTER_EXPANDER_X,
        ICON_Y,
        HEADER_ICON_SIZE,
        HEADER_ICON_SIZE
      )
    : Rect.IMPOSSIBLE;

  return (
    <>
      {representationData && (
        <g
          className={visualDiffClass}
          style={{
            fill,
            color: fill,
            stroke: 'none',
            opacity: getElementOpacityIfTransparentized(isTransparentized),
          }}
        >
          {
            <SvgComponentRepresentation
              representationData={representationData}
              bounds={componentRepresentationBounds}
            />
          }
        </g>
      )}
      <g transform={`translate(${labelX} -${GROUP_PADDING})`}>
        <CollapsibleGroupHeaderLabel
          ref={textElementRef}
          $isTransparentized={isTransparentized}
        />
      </g>

      {fieldLabelAndValue && (
        <CollapsibleGroupHeaderLabel
          x={labelX}
          y={groupLabelHeight + fontSize}
          fontSize={fontSize}
          $isTransparentized={isTransparentized}
        >
          {fieldLabelAndValue}
        </CollapsibleGroupHeaderLabel>
      )}
      {otherLabelsData?.map((otherLabelData, index) => (
        <OtherLabelComponent
          key={index}
          groupLabelHeight={groupLabelHeight}
          isTransparentized={isTransparentized}
          {...otherLabelData}
        />
      ))}
    </>
  );
};

export { GroupHeaderLabel };
